import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import '@tabler/icons-webfont/dist/tabler-icons.css';

import Navbar from "./components/Navbar";
import FloatingContact from "./components/floatingcontact";
import Hero from "./components/Hero";
import Homepage from "./components/Homepage";

import UIUXPage from "./Pages/uiux";
import WebPage from "./Pages/web";
import AppdevPage from "./Pages/appdev";
import SaasPage from "./Pages/saas";
import Dmpage from "./Pages/dm";
import GdPage from "./Pages/gd";
import VideoPage from "./Pages/video";

import AboutusPage from "./Pages/aboutus";
import TeamPage from "./Pages/team";
import BlogsPage from "./Pages/blogs";
import CareerPage from "./Pages/career";

import TermsPage from "./Pages/TermsPage";
import PrivacyPage from "./Pages/PrivacyPage";
import PaymentPolicy from "./Pages/PaymentPolicy";
import JobDetails from "./components/jobdetails";

import ScrollToTop from "./Pages/ScrollToTop";
import "./Style/transition.css";
import DeleteAccount from "./components/DeleteAccount";
import ClientProjects from "./Pages/ClientProjects";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── Page transition variants ─────────────────────────────────────────────────
const pageVariants = {
  initial: { opacity: 0, y: 28 },
  animate: {
    opacity: 1, y: 0,
    transition: { duration: 0.55, ease: [0.25, 0.1, 0.25, 1] },
  },
  exit: {
    opacity: 0, y: -14,
    transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
  },
};

// ─── Cinematic First-Load Loader ──────────────────────────────────────────────
const CinematicLoader = ({ phase }) => {
  const curtainsOut = phase === "out";
  const brandVisible = phase === "hold" || phase === "out";

  return (
    <div className="mp-loader">
      <div className="mp-noise" aria-hidden="true" />
      <div className={`mp-scan ${brandVisible ? "mp-scan--active" : ""}`} aria-hidden="true" />

      <div className="mp-corner mp-corner--tl" aria-hidden="true" />
      <div className="mp-corner mp-corner--tr" aria-hidden="true" />
      <div className="mp-corner mp-corner--bl" aria-hidden="true" />
      <div className="mp-corner mp-corner--br" aria-hidden="true" />

      <div className={`mp-curtain mp-curtain--top    ${curtainsOut ? "mp-curtain--open" : ""}`} />
      <div className={`mp-curtain mp-curtain--bottom ${curtainsOut ? "mp-curtain--open" : ""}`} />

      <div className="mp-brand" role="status" aria-label="Loading MPeoples Business Solutions">
        <div className={`mp-brand__m ${brandVisible ? "mp-brand__m--show" : ""}`}>
          <span><img src="/assets/logo.png" alt="MPeoples Logo" /></span>
        </div>
        <div className="mp-brand__tagline-wrap">
          <div className={`mp-brand__tagline ${brandVisible ? "mp-brand__tagline--show" : ""}`}>
            MPeoples Business Solutions Pvt. Ltd.
          </div>
        </div>
        <div className={`mp-brand__rule ${brandVisible ? "mp-brand__rule--show" : ""}`} />
        <div className={`mp-brand__bar ${brandVisible ? "mp-brand__bar--show" : ""}`}>
          <div className={`mp-brand__bar-fill ${brandVisible ? "mp-brand__bar-fill--run" : ""}`} />
        </div>
      </div>

      <div className={`mp-est ${brandVisible ? "mp-est--show" : ""}`} aria-hidden="true">
        Est. 2020
      </div>
    </div>
  );
};

// ─── Simple Route Progress Bar ────────────────────────────────────────────────
const RouteBar = () => (
  <div className="mp-routebar">
    <div className="mp-routebar__fill" />
  </div>
);

function AnimatedPage({ children }) {
  return (
    <motion.div
      initial="initial" animate="animate" exit="exit"
      variants={pageVariants}
      style={{ width: "100%", minHeight: "100vh" }}
    >
      {children}
    </motion.div>
  );
}

// ─── App Content ──────────────────────────────────────────────────────────────
function AppContent() {
  const [loaderPhase, setLoaderPhase] = useState("in"); // first-load cinematic
  const [isRouting, setIsRouting] = useState(false);    // simple route bar
  const navigate = useNavigate();
  const location = useLocation();

  // ── First-load cinematic sequence ────────────────────────────────────────
  useEffect(() => {
    const runIntro = async () => {
      await sleep(200);
      setLoaderPhase("hold");
      await sleep(2000);
      setLoaderPhase("out");
      await sleep(950);
      setLoaderPhase(null);
    };
    runIntro();
  }, []);

  // ── Simple route bar on navigation ───────────────────────────────────────
  const handleNavigation = async (path) => {
    if (location.pathname === path) return;

    setIsRouting(true);
    await sleep(500); // bar sweeps across
    navigate(path);
    await sleep(300);
    setIsRouting(false);
  };

  return (
    <>
      <ScrollToTop />
      <Navbar onNavigate={handleNavigation} />
      <FloatingContact />

      {/* First-load cinematic loader */}
      <AnimatePresence>
        {loaderPhase && (
          <CinematicLoader key="cinematic-loader" phase={loaderPhase} />
        )}
      </AnimatePresence>

      {/* Simple top bar for routing */}
      <AnimatePresence>
        {isRouting && <RouteBar key="routebar" />}
      </AnimatePresence>

      {/* Pages */}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <AnimatedPage>
                {/* Hero is always mounted (so it occupies its slot in the
                    DOM from the very first paint and Homepage never gets
                    shown "first"). Its own entrance timeline (curtain →
                    video settle → logo → headline → description → buttons)
                    stays paused via CSS until the App-level cinematic
                    loader has fully cleared, then runs from the start. */}
                <Hero startAnimation={loaderPhase === null} />
                <Homepage />
              </AnimatedPage>
            }
          />
          <Route path="/uiux-design" element={<AnimatedPage><UIUXPage /></AnimatedPage>} />
          <Route path="/web-development" element={<AnimatedPage><WebPage /></AnimatedPage>} />
          <Route path="/app-development" element={<AnimatedPage><AppdevPage /></AnimatedPage>} />
          <Route path="/saas-development" element={<AnimatedPage><SaasPage /></AnimatedPage>} />
          <Route path="/digital-marketing" element={<AnimatedPage><Dmpage /></AnimatedPage>} />
          <Route path="/graphic-design" element={<AnimatedPage><GdPage /></AnimatedPage>} />
          <Route path="/video-editing" element={<AnimatedPage><VideoPage /></AnimatedPage>} />
          <Route path="/about-us" element={<AnimatedPage><AboutusPage /></AnimatedPage>} />
          <Route path="/team-page" element={<AnimatedPage><TeamPage /></AnimatedPage>} />
          <Route path="/blogs-page" element={<AnimatedPage><BlogsPage /></AnimatedPage>} />
          <Route path="/career-page" element={<AnimatedPage><CareerPage /></AnimatedPage>} />
          <Route path="/termsandconditions/" element={<AnimatedPage><TermsPage /></AnimatedPage>} />
          <Route path="/privacypolicy/" element={<AnimatedPage><PrivacyPage /></AnimatedPage>} />
          <Route path="/paymentpolicy/" element={<AnimatedPage><PaymentPolicy /></AnimatedPage>} />
          <Route path="/job-details/:role" element={<AnimatedPage><JobDetails /></AnimatedPage>} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/client-projects" element={<AnimatedPage><ClientProjects /></AnimatedPage>} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;